import React, { useState, useEffect, useCallback } from 'react';
import NotificationRulesImportModal from '../components/NotificationRulesImportModal';
import { useSearchParams } from 'react-router-dom';
import { usePractice } from '../context/PracticeContext';
import api from '../services/api';

// =====================================================
// Shared constants
// =====================================================

const TRIGGER_TYPES = [
  { value: 'before_appt', label: 'Before Appointment' },
  { value: 'dayof_time', label: 'At Time on Day-of' },
  { value: 'on_event', label: 'On Event' },
];

const TRIGGER_UNITS = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
];

const EVENT_TYPES = [
  { value: 'on_create', label: 'Appointment Created' },
  { value: 'on_cancel', label: 'Cancellation' },
  { value: 'on_reschedule', label: 'Reschedule' },
];

// Human-readable labels for action_type values displayed in Activity tab
const ACTION_TYPE_LABELS = {
  'CONFIRM_CREATE_SMS': 'Confirmation SMS',
  'CONFIRM_CREATE_EMAIL': 'Confirmation Email',
  'CONFIRM_CREATE_VOICE': 'Confirmation Voice',
  'REMIND_SMS': 'Reminder SMS',
  'REMIND_EMAIL': 'Reminder Email',
  'REMIND_VOICE': 'Reminder Voice',
  'RESCHEDULE_SMS': 'Reschedule SMS',
  'RESCHEDULE_EMAIL': 'Reschedule Email',
  'RESCHEDULE_VOICE': 'Reschedule Voice',
  'CANCELLATION_SMS': 'Cancellation SMS',
  'CANCELLATION_EMAIL': 'Cancellation Email',
  'CANCELLATION_VOICE': 'Cancellation Voice',
  'FOLLOWUP_SMS': 'Follow-up SMS',
  'FOLLOWUP_EMAIL': 'Follow-up Email',
  'FOLLOWUP_VOICE': 'Follow-up Voice',
  'NO_SHOW_SMS': 'No-show SMS',
  'NO_SHOW_EMAIL': 'No-show Email',
  'NO_SHOW_VOICE': 'No-show Voice',
  'MANUAL_SMS': 'Manual SMS',
  'MANUAL_EMAIL': 'Manual Email',
  'MANUAL_VOICE': 'Manual Voice',
};

function formatActionType(actionType) {
  if (!actionType) return '--';
  return ACTION_TYPE_LABELS[actionType] || actionType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

const CHANNEL_OPTIONS = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'VOICE', label: 'Voice' },
];

const TEMPLATE_VARIABLES = [
  '{{patient_first_name}}',
  '{{patient_last_name}}',
  '{{appointment_date}}',
  '{{appointment_time}}',
  '{{appointment_type}}',
  '{{provider_name}}',
  '{{department_name}}',
  '{{practice_name}}',
  '{{practice_phone}}',
  '{{practice_logo}}',
  '{{practice_logo_url}}',
  '{{intake_link}}',
  '{{telehealth_link}}',
  '{{booking_url}}',
  // ad-hoc form sends only (outbound #254)
  '{{adhoc_link}}',
  '{{expires_date}}',
  '{{reason}}',
  '{{custom_message}}',
];

const CONDITION_FIELDS = [
  { value: 'patient.last_visit_date', label: 'Patient: Last Visit' },
  { value: 'patient.preferred_language', label: 'Patient: Language' },
  { value: 'patient.gender', label: 'Patient: Gender' },
  { value: 'patient.insurance_carrier', label: 'Patient: Insurance' },
  { value: 'patient.email', label: 'Patient: Email' },
  { value: 'patient.phone', label: 'Patient: Phone' },
  { value: 'patient.date_of_birth', label: 'Patient: DOB' },
  { value: 'appointment.appointment_type', label: 'Appt: Type' },
  { value: 'appointment.status', label: 'Appt: Status' },
  { value: 'appointment.duration_minutes', label: 'Appt: Duration' },
  { value: 'appointment.booked_by', label: 'Appt: Booked By' },
  { value: 'appointment.provider_name', label: 'Appt: Provider' },
  { value: 'appointment.department_name', label: 'Appt: Department' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
  { value: 'greater_than', label: 'greater than' },
  { value: 'less_than', label: 'less than' },
  { value: 'starts_with', label: 'starts with' },
];

const SMART_CONDITIONS = [
  { label: 'New Patient', conditions: [{ field: 'patient.last_visit_date', operator: 'is_null', value: null }] },
  { label: 'Returning', conditions: [{ field: 'patient.last_visit_date', operator: 'is_not_null', value: null }] },
  { label: 'Spanish', conditions: [{ field: 'patient.preferred_language', operator: 'equals', value: 'es' }] },
  { label: 'Telehealth', conditions: [{ field: 'appointment.appointment_type', operator: 'contains', value: 'telehealth' }] },
  { label: 'In-Person', conditions: [{ field: 'appointment.appointment_type', operator: 'not_contains', value: 'telehealth' }] },
];

const OPERATORS_NEEDING_VALUE = ['equals', 'not_equals', 'contains', 'not_contains', 'greater_than', 'less_than', 'starts_with'];

// =====================================================
// Shared inline styles
// =====================================================

const styles = {
  container: {
    padding: 24,
    fontFamily: 'inherit',
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },
  tabBar: {
    display: 'flex',
    gap: 8,
    marginBottom: 24,
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: 12,
  },
  tab: {
    padding: '8px 18px',
    borderRadius: 9999,
    border: '1px solid #d1d5db',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    transition: 'all 0.15s',
  },
  tabActive: {
    background: '#4f46e5',
    color: '#fff',
    borderColor: '#4f46e5',
  },
  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 20,
    background: '#f9fafb',
    marginBottom: 16,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    background: '#f3f4f6',
    borderBottom: '2px solid #e5e7eb',
    fontWeight: 600,
    color: '#374151',
    fontSize: 13,
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    color: '#374151',
  },
  trEven: {
    background: '#f9fafb',
  },
  trOdd: {
    background: '#fff',
  },
  btn: {
    padding: '7px 16px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    transition: 'opacity 0.15s',
  },
  btnPrimary: {
    background: '#4f46e5',
    color: '#fff',
  },
  btnSuccess: {
    background: '#059669',
    color: '#fff',
  },
  btnDanger: {
    background: '#dc2626',
    color: '#fff',
  },
  btnWarning: {
    background: '#d97706',
    color: '#fff',
  },
  btnSecondary: {
    background: '#e5e7eb',
    color: '#374151',
  },
  btnSmall: {
    padding: '4px 10px',
    fontSize: 12,
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeGreen: {
    background: '#d1fae5',
    color: '#059669',
  },
  badgeRed: {
    background: '#fee2e2',
    color: '#dc2626',
  },
  badgeGray: {
    background: '#f3f4f6',
    color: '#6b7280',
  },
  badgeAmber: {
    background: '#fef3c7',
    color: '#d97706',
  },
  badgeIndigo: {
    background: '#e0e7ff',
    color: '#4f46e5',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
    background: '#fff',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: 100,
    outline: 'none',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    fontSize: 13,
    marginBottom: 4,
    color: '#374151',
  },
  formGroup: {
    marginBottom: 16,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    padding: 28,
    width: '100%',
    maxWidth: 880,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 20,
    color: '#111827',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '10px 14px',
    borderRadius: 6,
    marginBottom: 12,
    fontSize: 13,
  },
  success: {
    background: '#d1fae5',
    color: '#059669',
    padding: '10px 14px',
    borderRadius: 6,
    marginBottom: 12,
    fontSize: 13,
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 14,
    marginBottom: 20,
  },
  summaryCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 16,
    background: '#fff',
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#111827',
  },
  summaryLabel: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 4,
  },
  searchBar: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
    alignItems: 'center',
  },
  helperText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
  },
  subTabBar: {
    display: 'flex',
    gap: 6,
    marginBottom: 18,
  },
  subTab: {
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
  },
  subTabActive: {
    background: '#4f46e5',
    color: '#fff',
    borderColor: '#4f46e5',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 12,
    marginTop: 8,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
    fontSize: 13,
    color: '#374151',
  },
};

// =====================================================
// Rules Tab
// =====================================================

const DEFAULT_REMINDER_RULE = {
  rule_name: 'Default Notifications',
  department_ids: null,
  provider_ids: null,
  appointment_types: null,
  is_active: 1,
  actions: [
    { action_type: 'CONFIRM_CREATE_SMS', is_enabled: 1, trigger_type: 'on_create', trigger_value: null, trigger_unit: null },
    { action_type: 'CONFIRM_CREATE_EMAIL', is_enabled: 1, trigger_type: 'on_create', trigger_value: null, trigger_unit: null },
    { action_type: 'REMIND_SMS', is_enabled: 1, trigger_type: 'before_appt', trigger_value: 24, trigger_unit: 'hours' },
    { action_type: 'REMIND_EMAIL', is_enabled: 1, trigger_type: 'before_appt', trigger_value: 48, trigger_unit: 'hours' },
    { action_type: 'REMIND_SMS', is_enabled: 1, trigger_type: 'before_appt', trigger_value: 2, trigger_unit: 'hours' },
  ],
};

function RulesTab({ practiceId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [setupBannerDismissed, setSetupBannerDismissed] = useState(false);
  const [creatingDefault, setCreatingDefault] = useState(false);
  const [rules, setRules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [voiceScripts, setVoiceScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false); // commons #256: Excel import
  const [editingRule, setEditingRule] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({
    rule_name: '',
    department_ids: [],
    provider_ids: [],
    appointment_types: [],
    actions: [],
  });
  const [reevalStats, setReevalStats] = useState(null);

  const loadReevalStats = useCallback(async () => {
    try {
      const res = await api.getNotificationReevaluateStats(practiceId);
      setReevalStats(res?.data || null);
    } catch { /* non-blocking */ }
  }, [practiceId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rulesRes, deptsRes, provsRes, typesRes, emailTplRes, smsTplRes, voiceTplRes] = await Promise.all([
        api.getNotificationRules(practiceId),
        api.getDepartments(practiceId),
        api.getProviders(practiceId),
        api.getNotificationAppointmentTypes(practiceId),
        api.getNotificationTemplates(practiceId, 'email'),
        api.getNotificationTemplates(practiceId, 'sms'),
        api.getNotificationTemplates(practiceId, 'voice'),
      ]);
      setRules(rulesRes.data || []);
      setDepartments(deptsRes.data || []);
      setProviders(provsRes.data || []);
      setAppointmentTypes(typesRes.data || []);
      setEmailTemplates(emailTplRes.data || []);
      setSmsTemplates(smsTplRes.data || []);
      setVoiceScripts(voiceTplRes.data || []);
      loadReevalStats();
    } catch (err) {
      setError(err.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [practiceId, loadReevalStats]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearMessages = () => { setError(null); setSuccess(null); };

  const openCreate = () => {
    clearMessages();
    setEditingRule(null);
    setForm({ rule_name: '', department_ids: [], provider_ids: [], appointment_types: [], actions: [] });
    setShowModal(true);
  };

  const [reevaluating, setReevaluating] = useState(false);
  const handleReevaluate = async () => {
    clearMessages();
    const ok = window.confirm(
      'Re-evaluate notification rules for all future appointments?\n\n' +
      'Use this after adding or editing rules — it re-schedules reminders for appointments that were synced before the rules existed. Already-sent notifications are not re-sent.'
    );
    if (!ok) return;
    setReevaluating(true);
    try {
      const res = await api.reevaluateNotificationRules(practiceId);
      const s = res?.data || {};
      setSuccess(
        `Re-evaluated ${s.appointmentsProcessed || 0} future appointment(s): ` +
        `${s.matched || 0} matched a rule, ${s.noMatch || 0} had no matching rule` +
        (s.errors ? `, ${s.errors} error(s)` : '') + '.'
      );
      loadReevalStats();
    } catch (err) {
      setError(err.message || 'Re-evaluate failed');
    } finally {
      setReevaluating(false);
    }
  };

  // ── Shared helpers for openEdit / handleDuplicate ──

  const LEGACY_ACTION_MAP = {
    'DIGITAL_FORMS': { type: 'before_appt', value: '72', unit: 'hours' },
    'DYNAMIC_FORMS': { type: 'before_appt', value: '72', unit: 'hours' },
    'REMINDER_96HR': { type: 'before_appt', value: '96', unit: 'hours' },
    'REMINDER_48HR': { type: 'before_appt', value: '48', unit: 'hours' },
    'REMINDER_24HR': { type: 'before_appt', value: '24', unit: 'hours' },
    'REMINDER_8AM': { type: 'dayof_time', value: '08:00', unit: 'hours' },
    'TELEMED_5MIN': { type: 'before_appt', value: '5', unit: 'minutes' },
    'CONFIRM_CREATE': { type: 'on_create', value: '', unit: 'hours' },
    'CANCELLATION': { type: 'on_event', value: 'on_cancel', unit: 'hours' },
    'RESCHEDULE': { type: 'on_event', value: 'on_reschedule', unit: 'hours' },
  };

  const groupActionsForEditor = (actions) => {
    const grouped = {};
    for (const a of (actions || [])) {
      const parts = (a.action_type || '').split('_');
      let channel = parts.pop();
      // Handle counter-suffixed action_types from legacy dedup bug (e.g., RESCHEDULE_SMS_2)
      if (!['SMS', 'EMAIL', 'VOICE'].includes(channel)) {
        channel = parts.pop() || channel;
      }
      const prefix = parts.join('_');

      let trigger_type = a.trigger_type || 'before_appt';
      let trigger_value = a.trigger_value || '';
      let trigger_unit = a.trigger_unit || 'hours';

      if (!a.trigger_type || a.trigger_type === 'legacy') {
        const mapped = LEGACY_ACTION_MAP[prefix];
        if (mapped) {
          trigger_type = mapped.type;
          trigger_value = mapped.value;
          trigger_unit = mapped.unit;
        }
      }

      const key = `${trigger_type}|${trigger_value}|${trigger_unit}`;
      if (!grouped[key]) {
        let conditions = [];
        if (a.conditions_json) {
          try { conditions = JSON.parse(a.conditions_json); } catch {}
        }
        grouped[key] = {
          trigger_type, trigger_value, trigger_unit,
          channels: [], templates: {},
          // The id of the row each channel came from, carried back on save so the
          // server can tell "this action, re-timed" from "that action deleted and a
          // different one added". Without it an edit from 24h to 23h looked like a
          // different action entirely: the old row was deleted, its queued reminders
          // were cancelled as "Rule changed", and the new row started with none.
          ids: {},
          is_enabled: a.is_enabled, conditions,
          condition_mode: a.condition_mode || 'ALL',
        };
      }
      if (channel && !grouped[key].channels.includes(channel)) {
        grouped[key].channels.push(channel);
      }
      if (channel && a.id != null) {
        grouped[key].ids[channel] = a.id;
      }
      const tplId = a.email_template_id || a.sms_template_id || a.voice_script_id || null;
      if (tplId && channel) {
        grouped[key].templates[channel] = tplId;
      }
    }
    return Object.values(grouped);
  };

  const parseRuleScope = (rule) => {
    let deptIds = [];
    if (rule.department_ids) { try { deptIds = JSON.parse(rule.department_ids); } catch {} }
    else if (rule.department_id) { deptIds = [rule.department_id]; }

    let provIds = [];
    if (rule.provider_ids) { try { provIds = JSON.parse(rule.provider_ids); } catch {} }
    else if (rule.provider_id) { provIds = [rule.provider_id]; }

    let apptTypes = [];
    if (rule.appointment_types) { try { apptTypes = JSON.parse(rule.appointment_types); } catch {} }
    else if (rule.appointment_type) { apptTypes = [rule.appointment_type]; }

    return { deptIds, provIds, apptTypes };
  };

  const openEdit = (rule) => {
    clearMessages();
    setEditingRule(rule);

    const actionObjects = groupActionsForEditor(rule.actions);
    const { deptIds, provIds, apptTypes } = parseRuleScope(rule);

    setForm({ rule_name: rule.rule_name || '', department_ids: deptIds, provider_ids: provIds, appointment_types: apptTypes, actions: actionObjects });
    setShowModal(true);
  };

  // ── Dynamic Action Builder helpers ──

  const addAction = () => {
    setForm(prev => ({
      ...prev,
      actions: [...prev.actions, {
        trigger_type: 'before_appt', trigger_value: '', trigger_unit: 'hours',
        channels: [], templates: {}, is_enabled: 1, conditions: [], condition_mode: 'ALL'
      }]
    }));
  };

  const removeAction = (idx) => {
    setForm(prev => ({ ...prev, actions: prev.actions.filter((_, i) => i !== idx) }));
  };

  const updateAction = (idx, updates) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === idx ? { ...a, ...updates } : a)
    }));
  };

  const getTemplatesForChannel = (channel) => {
    if (channel === 'EMAIL') return emailTemplates;
    if (channel === 'SMS') return smsTemplates;
    if (channel === 'VOICE') return voiceScripts;
    return [];
  };

  const addConditionToAction = (idx) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) =>
        i === idx ? { ...a, conditions: [...(a.conditions || []), { field: '', operator: 'equals', value: '' }] } : a
      )
    }));
  };
  const updateCondition = (actionIdx, condIdx, updates) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) =>
        i === actionIdx ? { ...a, conditions: (a.conditions || []).map((c, ci) => ci === condIdx ? { ...c, ...updates } : c) } : a
      )
    }));
  };
  const removeCondition = (actionIdx, condIdx) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) =>
        i === actionIdx ? { ...a, conditions: (a.conditions || []).filter((_, ci) => ci !== condIdx) } : a
      )
    }));
  };
  const applySmartCondition = (actionIdx, smartCondition) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) =>
        i === actionIdx ? { ...a, conditions: [...(a.conditions || []), ...smartCondition.conditions] } : a
      )
    }));
  };
  const setConditionMode = (actionIdx, mode) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === actionIdx ? { ...a, condition_mode: mode } : a)
    }));
  };

  const handleSave = async () => {
    clearMessages();
    if (!form.rule_name.trim()) {
      setError('Rule name is required');
      return;
    }
    // Validate actions
    for (const a of form.actions) {
      if (!a.trigger_type) {
        setError('Each action must have a trigger type selected');
        return;
      }
      if (!a.channels || a.channels.length === 0) {
        setError('Each action must have at least one channel selected');
        return;
      }
      if (a.trigger_type === 'before_appt' && (!a.trigger_value || parseFloat(a.trigger_value) <= 0)) {
        setError('Each "Before Appointment" action needs a time value greater than 0');
        return;
      }
      if (a.trigger_type === 'dayof_time' && !a.trigger_value) {
        setError('Each "At Time on Day-of" action needs a time value');
        return;
      }
      if (a.trigger_type === 'on_event' && !a.trigger_value) {
        setError('Each "On Event" action needs an event type selected');
        return;
      }
    }
    // Prevent fully duplicate actions (same trigger + same channels)
    const triggerSigs = new Set();
    for (const a of form.actions) {
      const channels = (a.channels || []).sort().join(',');
      const sig = a.trigger_type === 'on_event'
        ? `on_event|${a.trigger_value}|${channels}`
        : a.trigger_type === 'on_create'
          ? `on_create|${channels}`
          : `${a.trigger_type}|${a.trigger_value}|${a.trigger_unit}|${channels}`;
      if (triggerSigs.has(sig)) {
        setError('Duplicate action found (same trigger and channels). Change the channels or trigger timing to differentiate.');
        return;
      }
      triggerSigs.add(sig);
    }
    try {
      // Fan out: one UI action with multiple channels → one backend action per channel
      const expandedActions = [];
      for (const a of form.actions) {
        let actionLabel;
        if (a.trigger_type === 'on_create') {
          actionLabel = 'CONFIRM_CREATE';
        } else if (a.trigger_type === 'before_appt') {
          actionLabel = `BEFORE_${a.trigger_value}${(a.trigger_unit || 'hours').charAt(0).toUpperCase()}`;
        } else if (a.trigger_type === 'dayof_time') {
          actionLabel = `DAYOF_${(a.trigger_value || '08:00').replace(':', '')}`;
        } else if (a.trigger_type === 'on_event') {
          actionLabel = a.trigger_value === 'on_cancel' ? 'CANCELLATION' : a.trigger_value === 'on_create' ? 'CONFIRMATION' : 'RESCHEDULE';
        } else {
          actionLabel = 'CUSTOM';
        }
        for (const ch of (a.channels || [])) {
          const actionType = `${actionLabel}_${ch}`;
          const tplId = (a.templates && a.templates[ch]) ? parseInt(a.templates[ch]) : null;
          expandedActions.push({
            // null for a channel the practice just added — the server inserts those.
            id: (a.ids && a.ids[ch] != null) ? a.ids[ch] : null,
            action_type: actionType,
            is_enabled: a.is_enabled ?? 1,
            email_template_id: ch === 'EMAIL' ? tplId : null,
            sms_template_id: ch === 'SMS' ? tplId : null,
            voice_script_id: ch === 'VOICE' ? tplId : null,
            conditions_json: (a.conditions && a.conditions.length > 0) ? a.conditions : null,
            condition_mode: a.condition_mode || 'ALL',
            trigger_type: a.trigger_type,
            trigger_value: String(a.trigger_value || ''),
            trigger_unit: a.trigger_unit || 'hours',
          });
        }
      }

      const payload = {
        rule_name: form.rule_name,
        department_ids: form.department_ids.length > 0 ? form.department_ids : null,
        provider_ids: form.provider_ids.length > 0 ? form.provider_ids : null,
        appointment_types: form.appointment_types.length > 0 ? form.appointment_types : null,
        actions: expandedActions,
      };
      let result;
      if (editingRule) {
        result = await api.updateNotificationRule(practiceId, editingRule.id, payload);
        setSuccess('Rule updated');
      } else {
        result = await api.createNotificationRule(practiceId, payload);
        setSuccess('Rule created');
      }
      // Show conflict warnings for overlapping (but not identical) scopes
      if (result && result.data && result.data.conflict_warnings && result.data.conflict_warnings.length > 0) {
        const names = result.data.conflict_warnings.map(w => `"${w.rule_name}"`).join(', ');
        alert(`Warning: This rule overlaps with existing rules: ${names}. Patients may receive duplicate notifications.`);
      }
      // Show zombie-reference warnings — provider/department rows not linked to Athena.
      // A rule pointing at these will never match real synced appointments.
      if (result && result.data && result.data.zombie_warnings) {
        const z = result.data.zombie_warnings;
        const provNames = (z.providers || []).map(p => `${p.first_name} ${p.last_name}`).join(', ');
        const deptNames = (z.departments || []).map(d => d.name).join(', ');
        const parts = [];
        if (provNames) parts.push(`provider(s) not linked to Athena: ${provNames}`);
        if (deptNames) parts.push(`department(s) not linked to Athena: ${deptNames}`);
        if (parts.length > 0) {
          alert(
            `Warning: This rule references ${parts.join('; ')}.\n\n` +
            `Rules pointing at unlinked rows will never match real appointments. ` +
            `Re-pick the Athena-synced entries in the dropdown, or sync these providers/departments first.`
          );
        }
      }
      setShowModal(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to save rule');
    }
  };

  const handleToggle = async (rule) => {
    clearMessages();
    try {
      await api.toggleNotificationRule(practiceId, rule.id);
      setSuccess(`Rule ${rule.is_active ? 'deactivated' : 'activated'}`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to toggle rule');
    }
  };

  const handleDuplicate = (rule) => {
    clearMessages();
    // Open editor pre-filled with a copy — user modifies scope/actions before saving as new rule
    setEditingRule(null); // null = create mode

    const actionObjects = groupActionsForEditor(rule.actions);
    const { deptIds, provIds, apptTypes } = parseRuleScope(rule);

    setForm({
      rule_name: `${rule.rule_name || ''} (Copy)`,
      department_ids: deptIds,
      provider_ids: provIds,
      appointment_types: apptTypes,
      actions: actionObjects,
    });
    setShowModal(true);
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.rule_name}"? This cannot be undone.`)) return;
    clearMessages();
    try {
      await api.deleteNotificationRule(practiceId, rule.id);
      setSuccess('Rule deleted');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete rule');
    }
  };

  const getDeptNames = (rule) => {
    let ids = [];
    if (rule.department_ids) { try { ids = JSON.parse(rule.department_ids); } catch {} }
    else if (rule.department_id) { ids = [rule.department_id]; }
    if (ids.length === 0) return 'Any';
    return ids.map(id => { const d = departments.find(dept => dept.id === id || String(dept.id) === String(id)); return d ? d.name : id; }).join(', ');
  };

  const getProvNames = (rule) => {
    let ids = [];
    if (rule.provider_ids) { try { ids = JSON.parse(rule.provider_ids); } catch {} }
    else if (rule.provider_id) { ids = [rule.provider_id]; }
    if (ids.length === 0) return 'Any';
    return ids.map(id => { const p = providers.find(prov => prov.id === id || String(prov.id) === String(id)); return p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : id; }).join(', ');
  };

  const getApptTypes = (rule) => {
    let types = [];
    if (rule.appointment_types) { try { types = JSON.parse(rule.appointment_types); } catch {} }
    else if (rule.appointment_type) { types = [rule.appointment_type]; }
    return types.length > 0 ? types.join(', ') : 'Any';
  };

  // Client-side search filter
  const filteredRules = searchQuery.trim()
    ? rules.filter(r => {
        const q = searchQuery.toLowerCase();
        return (r.rule_name || '').toLowerCase().includes(q) ||
          getDeptNames(r).toLowerCase().includes(q) ||
          getProvNames(r).toLowerCase().includes(q) ||
          getApptTypes(r).toLowerCase().includes(q);
      })
    : rules;

  const handleQuickSetupRules = async () => {
    try {
      setCreatingDefault(true);
      const res = await api.createNotificationRule(practiceId, DEFAULT_REMINDER_RULE);
      if (res.success) {
        await loadData();
        setSearchParams({});
        setSetupBannerDismissed(true);
      } else {
        setError(res.error || 'Failed to create default rule');
      }
    } catch (err) {
      setError(err.message || 'Failed to create default rule');
    } finally {
      setCreatingDefault(false);
    }
  };

  const showSetupBanner = searchParams.get('setup') === 'true'
    && rules.length === 0
    && !setupBannerDismissed
    && !loading;

  if (loading) return <div style={{ padding: 20, color: '#6b7280' }}>Loading rules...</div>;

  return (
    <div>
      {showSetupBanner && (
        <div style={setupBannerS.banner}>
          <div style={setupBannerS.iconWrap}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={setupBannerS.title}>Set Up Appointment Reminders</h3>
            <p style={setupBannerS.desc}>
              Create a default notification rule with SMS confirmations, 48h email reminder, and 24h + 2h SMS reminders.
            </p>
          </div>
          <div style={setupBannerS.actions}>
            <button style={setupBannerS.primaryBtn} onClick={handleQuickSetupRules} disabled={creatingDefault}>
              {creatingDefault ? 'Creating...' : 'Create Default Rule'}
            </button>
            <button style={setupBannerS.dismissBtn} onClick={() => { setSetupBannerDismissed(true); setSearchParams({}); }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {reevalStats && reevalStats.without_notifications > 0 && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <div style={{ color: '#78350f', fontSize: 14 }}>
            <strong>{reevalStats.without_notifications}</strong> of {reevalStats.future_appointments} upcoming appointment(s) have no scheduled reminders.
            This usually happens when appointments were synced before rules were created or edited.
          </div>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary, opacity: reevaluating ? 0.6 : 1 }}
            onClick={handleReevaluate}
            disabled={reevaluating}
          >
            {reevaluating ? 'Re-evaluating…' : 'Re-evaluate now'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <h3 style={styles.sectionTitle}>Notification Rules</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
          <input
            style={{ ...styles.input, maxWidth: 260, margin: 0 }}
            placeholder="Search rules..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <button
            style={{ ...styles.btn, ...styles.btnSecondary, opacity: reevaluating ? 0.6 : 1 }}
            onClick={handleReevaluate}
            disabled={reevaluating}
            title="Re-schedule reminders for future appointments that were synced before the current rules existed"
          >
            {reevaluating ? 'Re-evaluating…' : 'Re-evaluate upcoming appts'}
          </button>
          <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowImport(true)} title="Create rules from the practice's notification-rules Excel workbook">
            Import from Excel
          </button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={openCreate}>
            + Create Rule
          </button>
        </div>
      </div>

      {showImport && (
        <NotificationRulesImportModal
          practiceId={practiceId}
          providers={providers}
          emailTemplates={emailTemplates}
          smsTemplates={smsTemplates}
          onClose={() => setShowImport(false)}
          onImported={(r) => { setSuccess(`Imported ${r.created.length} rules`); loadData(); }}
        />
      )}

      {filteredRules.length === 0 ? (
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>{rules.length === 0 ? 'No rules configured. Create your first notification rule.' : 'No rules match your search.'}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Rule Name</th>
                <th style={styles.th}>Departments</th>
                <th style={styles.th}>Providers</th>
                <th style={styles.th}>Appointment Types</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRules.map((rule, idx) => (
                <tr key={rule.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                  <td style={styles.td}>{rule.rule_name}</td>
                  <td style={{ ...styles.td, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }} title={getDeptNames(rule)}>{getDeptNames(rule)}</td>
                  <td style={{ ...styles.td, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }} title={getProvNames(rule)}>{getProvNames(rule)}</td>
                  <td style={{ ...styles.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={getApptTypes(rule)}>{getApptTypes(rule)}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(rule.is_active ? styles.badgeGreen : styles.badgeGray) }}>
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.flexRow}>
                      <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary }} onClick={() => openEdit(rule)}>Edit</button>
                      <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary }} onClick={() => handleDuplicate(rule)}>Duplicate</button>
                      <button style={{ ...styles.btn, ...styles.btnSmall, ...(rule.is_active ? styles.btnWarning : styles.btnSuccess) }} onClick={() => handleToggle(rule)}>
                        {rule.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnDanger }} onClick={() => handleDelete(rule)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.overlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{editingRule ? 'Edit Rule' : 'Create Rule'}</h3>

            <div style={styles.formGroup}>
              <label style={styles.label}>Rule Name</label>
              <input
                style={styles.input}
                value={form.rule_name}
                onChange={e => setForm(prev => ({ ...prev, rule_name: e.target.value }))}
                placeholder="e.g. Default Reminder Rule"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Departments <span style={{ color: '#9ca3af', fontWeight: 400 }}>(none = any)</span></label>
              <div style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: 8, maxHeight: 120, overflowY: 'auto', background: '#fff' }}>
                {departments.length === 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}>No departments found</span>}
                {departments.map(d => (
                  <label key={d.id} style={{ ...styles.checkboxRow, marginBottom: 2 }}>
                    <input type="checkbox"
                      checked={form.department_ids.includes(d.id) || form.department_ids.includes(String(d.id))}
                      onChange={e => {
                        setForm(prev => {
                          const ids = [...prev.department_ids];
                          if (e.target.checked) { ids.push(d.id); } else { const idx = ids.indexOf(d.id); const idx2 = ids.indexOf(String(d.id)); if (idx >= 0) ids.splice(idx, 1); if (idx2 >= 0) ids.splice(idx2, 1); }
                          return { ...prev, department_ids: ids };
                        });
                      }} />
                    <span style={{ fontSize: 13 }}>{d.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Providers <span style={{ color: '#9ca3af', fontWeight: 400 }}>(none = any)</span></label>
              <div style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: 8, maxHeight: 120, overflowY: 'auto', background: '#fff' }}>
                {providers.length === 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}>No providers found</span>}
                {providers.map(p => (
                  <label key={p.id} style={{ ...styles.checkboxRow, marginBottom: 2 }}>
                    <input type="checkbox"
                      checked={form.provider_ids.includes(p.id) || form.provider_ids.includes(String(p.id))}
                      onChange={e => {
                        setForm(prev => {
                          const ids = [...prev.provider_ids];
                          if (e.target.checked) { ids.push(p.id); } else { const idx = ids.indexOf(p.id); const idx2 = ids.indexOf(String(p.id)); if (idx >= 0) ids.splice(idx, 1); if (idx2 >= 0) ids.splice(idx2, 1); }
                          return { ...prev, provider_ids: ids };
                        });
                      }} />
                    <span style={{ fontSize: 13 }}>{p.first_name} {p.last_name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Appointment Types <span style={{ color: '#9ca3af', fontWeight: 400 }}>(none = any)</span></label>
              <div style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: 8, maxHeight: 120, overflowY: 'auto', background: '#fff' }}>
                {appointmentTypes.length === 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}>No appointment types found</span>}
                {appointmentTypes.map(t => (
                  <label key={t} style={{ ...styles.checkboxRow, marginBottom: 2 }}>
                    <input type="checkbox"
                      checked={form.appointment_types.includes(t)}
                      onChange={e => {
                        setForm(prev => {
                          const types = [...prev.appointment_types];
                          if (e.target.checked) { types.push(t); } else { const idx = types.indexOf(t); if (idx >= 0) types.splice(idx, 1); }
                          return { ...prev, appointment_types: types };
                        });
                      }} />
                    <span style={{ fontSize: 13 }}>{t}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Actions</label>
              {form.actions.length === 0 && (
                <p style={{ fontSize: 13, color: '#9ca3af', margin: '4px 0 8px' }}>No actions yet. Add one below.</p>
              )}
              {form.actions.map((action, idx) => {
                return (
                  <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12, background: '#f9fafb' }}>
                    {/* Header row: action # + remove */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Action {idx + 1}</span>
                      <button type="button" style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => removeAction(idx)}>Remove</button>
                    </div>

                    {/* Trigger type row */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div style={{ minWidth: 160 }}>
                        <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Trigger</label>
                        <select style={styles.select} value={action.trigger_type || 'before_appt'}
                          onChange={e => updateAction(idx, { trigger_type: e.target.value, trigger_value: '', trigger_unit: 'hours' })}>
                          {TRIGGER_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Dynamic inputs based on trigger type */}
                      {action.trigger_type === 'before_appt' && (
                        <>
                          <div style={{ minWidth: 80 }}>
                            <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Amount</label>
                            <input type="number" min="1" style={styles.input}
                              value={action.trigger_value || ''}
                              onChange={e => updateAction(idx, { trigger_value: e.target.value })}
                              placeholder="e.g. 21" />
                          </div>
                          <div style={{ minWidth: 100 }}>
                            <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Unit</label>
                            <select style={styles.select} value={action.trigger_unit || 'hours'}
                              onChange={e => updateAction(idx, { trigger_unit: e.target.value })}>
                              {TRIGGER_UNITS.map(u => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                            </select>
                          </div>
                          <span style={{ fontSize: 12, color: '#6b7280', paddingBottom: 8 }}>before appointment</span>
                        </>
                      )}

                      {action.trigger_type === 'dayof_time' && (
                        <div style={{ minWidth: 120 }}>
                          <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Time (practice TZ)</label>
                          <input type="time" style={styles.input}
                            value={action.trigger_value || '08:00'}
                            onChange={e => updateAction(idx, { trigger_value: e.target.value })} />
                        </div>
                      )}

                      {action.trigger_type === 'on_event' && (
                        <div style={{ minWidth: 160 }}>
                          <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Event</label>
                          <select style={styles.select} value={action.trigger_value || ''}
                            onChange={e => updateAction(idx, { trigger_value: e.target.value })}>
                            <option value="">-- Select event --</option>
                            {EVENT_TYPES.map(ev => (
                              <option key={ev.value} value={ev.value}>{ev.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Channel checkboxes + per-channel template */}
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 4 }}>Channels &amp; Templates</label>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {CHANNEL_OPTIONS.map(ch => {
                          const isChecked = (action.channels || []).includes(ch.value);
                          const channelTemplates = getTemplatesForChannel(ch.value);
                          return (
                            <div key={ch.value} style={{ minWidth: 180 }}>
                              <label style={{ ...styles.checkboxRow, marginBottom: 4 }}>
                                <input type="checkbox" checked={isChecked}
                                  onChange={e => {
                                    const channels = [...(action.channels || [])];
                                    if (e.target.checked) { channels.push(ch.value); } else { const i = channels.indexOf(ch.value); if (i >= 0) channels.splice(i, 1); }
                                    const templates = { ...(action.templates || {}) };
                                    if (!e.target.checked) delete templates[ch.value];
                                    updateAction(idx, { channels, templates });
                                  }} />
                                <span style={{ fontSize: 13, fontWeight: 500 }}>{ch.label}</span>
                              </label>
                              {isChecked && (
                                <select style={{ ...styles.select, fontSize: 12, padding: '4px 8px', marginLeft: 20 }}
                                  value={(action.templates && action.templates[ch.value]) || ''}
                                  onChange={e => {
                                    const templates = { ...(action.templates || {}), [ch.value]: e.target.value || null };
                                    updateAction(idx, { templates });
                                  }}>
                                  <option value="">Default message</option>
                                  {channelTemplates.map(t => (
                                    <option key={t.id} value={t.id}>{t.template_name || t.script_name}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Conditions section */}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Conditions</span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>(optional — leave empty to always fire)</span>
                        <span title="Conditions filter which appointments trigger this action. Example: limit to new patients only, specific appointment types, or certain providers. Use smart presets below for common filters." style={{ cursor: 'help', fontSize: 12, color: '#9ca3af', marginLeft: 2 }}>ⓘ</span>
                      </div>

                      {/* Smart condition presets */}
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                        {SMART_CONDITIONS.map(sc => (
                          <button key={sc.label} type="button"
                            style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }}
                            onClick={() => applySmartCondition(idx, sc)}>
                            +{sc.label}
                          </button>
                        ))}
                      </div>

                      {/* ALL/ANY toggle */}
                      {(action.conditions || []).length > 1 && (
                        <div style={{ fontSize: 12, marginBottom: 6, color: '#6b7280' }}>
                          Match:&nbsp;
                          <select style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 6px' }}
                            value={action.condition_mode || 'ALL'}
                            onChange={e => setConditionMode(idx, e.target.value)}>
                            <option value="ALL">ALL conditions (AND)</option>
                            <option value="ANY">ANY condition (OR)</option>
                          </select>
                        </div>
                      )}

                      {/* Condition rows */}
                      {(action.conditions || []).map((cond, ci) => (
                        <div key={ci} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                          <select style={{ ...styles.select, flex: 2, fontSize: 12, padding: '4px 6px' }}
                            value={cond.field} onChange={e => updateCondition(idx, ci, { field: e.target.value })}>
                            <option value="">-- Field --</option>
                            {CONDITION_FIELDS.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                          <select style={{ ...styles.select, flex: 1, fontSize: 12, padding: '4px 6px' }}
                            value={cond.operator} onChange={e => updateCondition(idx, ci, { operator: e.target.value })}>
                            {CONDITION_OPERATORS.map(op => (
                              <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                          </select>
                          {OPERATORS_NEEDING_VALUE.includes(cond.operator) && (
                            <input style={{ ...styles.input, flex: 1, fontSize: 12, padding: '4px 6px' }}
                              value={cond.value || ''} onChange={e => updateCondition(idx, ci, { value: e.target.value })}
                              placeholder="value" />
                          )}
                          <button type="button"
                            style={{ ...styles.btn, ...styles.btnDanger, padding: '2px 7px', fontSize: 12 }}
                            onClick={() => removeCondition(idx, ci)}>x</button>
                        </div>
                      ))}

                      <button type="button"
                        style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary, fontSize: 11, marginTop: 2 }}
                        onClick={() => addConditionToAction(idx)}>
                        + Custom Condition
                      </button>
                    </div>
                  </div>
                );
              })}
              <button type="button" style={{ ...styles.btn, ...styles.btnPrimary, width: '100%', marginTop: 4 }} onClick={addAction}>
                + Add Action
              </button>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.modalActions}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSave}>
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// Default template seeds
// =====================================================

const DEFAULT_SMS_TEMPLATES = [
  {
    template_name: 'Appointment Reminder',
    message_body: 'Hi {{patient_first_name}}, this is a reminder about your appointment with {{provider_name}} on {{appointment_date}} at {{appointment_time}}. Please call {{practice_phone}} if you need to reschedule. — {{practice_name}}',
  },
  {
    template_name: 'Day-of Reminder',
    message_body: 'Hi {{patient_first_name}}, your appointment is today at {{appointment_time}} with {{provider_name}} at {{practice_name}}. See you soon! Call {{practice_phone}} with questions.',
  },
  {
    template_name: 'Cancellation Notice',
    message_body: 'Hi {{patient_first_name}}, your appointment on {{appointment_date}} at {{appointment_time}} with {{provider_name}} has been canceled. Call {{practice_phone}} to rebook. — {{practice_name}}',
  },
  {
    template_name: 'Reschedule Confirmation',
    message_body: 'Hi {{patient_first_name}}, your appointment has been rescheduled to {{appointment_date}} at {{appointment_time}} with {{provider_name}}. Call {{practice_phone}} with questions. — {{practice_name}}',
  },
  {
    template_name: 'No-Show Follow Up',
    message_body: 'Hi {{patient_first_name}}, we missed you at your appointment today with {{provider_name}}. Please call {{practice_phone}} to reschedule. We look forward to seeing you! — {{practice_name}}',
  },
];

const DEFAULT_EMAIL_TEMPLATES = [
  {
    template_name: 'Appointment Reminder',
    subject: 'Reminder: Your upcoming appointment at {{practice_name}}',
    body_html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px">
  <h2 style="color:#1e40af">Appointment Reminder</h2>
  <p>Hi {{patient_first_name}},</p>
  <p>This is a friendly reminder about your upcoming appointment:</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0">
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Date:</td><td style="padding:8px">{{appointment_date}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Time:</td><td style="padding:8px">{{appointment_time}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Provider:</td><td style="padding:8px">{{provider_name}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Department:</td><td style="padding:8px">{{department_name}}</td></tr>
  </table>
  <p>If you need to reschedule or cancel, please call us at <strong>{{practice_phone}}</strong>.</p>
  <p>We look forward to seeing you!</p>
  <p style="color:#6b7280;margin-top:24px">— {{practice_name}}</p>
</div>`,
    body_text: 'Hi {{patient_first_name}},\n\nThis is a reminder about your upcoming appointment:\n\nDate: {{appointment_date}}\nTime: {{appointment_time}}\nProvider: {{provider_name}}\nDepartment: {{department_name}}\n\nIf you need to reschedule or cancel, please call us at {{practice_phone}}.\n\nWe look forward to seeing you!\n\n— {{practice_name}}',
  },
  {
    template_name: 'Day-of Reminder',
    subject: 'Your appointment is today at {{appointment_time}} — {{practice_name}}',
    body_html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px">
  <h2 style="color:#1e40af">Your Appointment is Today!</h2>
  <p>Hi {{patient_first_name}},</p>
  <p>Just a quick reminder that your appointment is <strong>today</strong>:</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0">
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Time:</td><td style="padding:8px">{{appointment_time}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Provider:</td><td style="padding:8px">{{provider_name}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Location:</td><td style="padding:8px">{{department_name}}</td></tr>
  </table>
  <p>Please arrive 10-15 minutes early. If you need to cancel, call us at <strong>{{practice_phone}}</strong>.</p>
  <p>See you soon!</p>
  <p style="color:#6b7280;margin-top:24px">— {{practice_name}}</p>
</div>`,
    body_text: 'Hi {{patient_first_name}},\n\nYour appointment is today!\n\nTime: {{appointment_time}}\nProvider: {{provider_name}}\nLocation: {{department_name}}\n\nPlease arrive 10-15 minutes early. Call {{practice_phone}} to cancel.\n\nSee you soon!\n— {{practice_name}}',
  },
  {
    template_name: 'Cancellation Notice',
    subject: 'Your appointment on {{appointment_date}} has been canceled',
    body_html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px">
  <h2 style="color:#dc2626">Appointment Canceled</h2>
  <p>Hi {{patient_first_name}},</p>
  <p>Your appointment has been canceled:</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0">
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Date:</td><td style="padding:8px">{{appointment_date}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Time:</td><td style="padding:8px">{{appointment_time}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Provider:</td><td style="padding:8px">{{provider_name}}</td></tr>
  </table>
  <p>To reschedule, please call us at <strong>{{practice_phone}}</strong>.</p>
  <p style="color:#6b7280;margin-top:24px">— {{practice_name}}</p>
</div>`,
    body_text: 'Hi {{patient_first_name}},\n\nYour appointment has been canceled:\n\nDate: {{appointment_date}}\nTime: {{appointment_time}}\nProvider: {{provider_name}}\n\nTo reschedule, please call us at {{practice_phone}}.\n\n— {{practice_name}}',
  },
  {
    template_name: 'Reschedule Confirmation',
    subject: 'Your appointment has been rescheduled — {{practice_name}}',
    body_html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px">
  <h2 style="color:#2563eb">Appointment Rescheduled</h2>
  <p>Hi {{patient_first_name}},</p>
  <p>Your appointment has been rescheduled to:</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0">
    <tr><td style="padding:8px;font-weight:bold;color:#374151">New Date:</td><td style="padding:8px">{{appointment_date}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">New Time:</td><td style="padding:8px">{{appointment_time}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Provider:</td><td style="padding:8px">{{provider_name}}</td></tr>
    <tr><td style="padding:8px;font-weight:bold;color:#374151">Department:</td><td style="padding:8px">{{department_name}}</td></tr>
  </table>
  <p>If this time doesn't work, call us at <strong>{{practice_phone}}</strong> and we'll find a better option.</p>
  <p style="color:#6b7280;margin-top:24px">— {{practice_name}}</p>
</div>`,
    body_text: 'Hi {{patient_first_name}},\n\nYour appointment has been rescheduled:\n\nNew Date: {{appointment_date}}\nNew Time: {{appointment_time}}\nProvider: {{provider_name}}\nDepartment: {{department_name}}\n\nCall {{practice_phone}} if this time doesn\'t work.\n\n— {{practice_name}}',
  },
  {
    template_name: 'No-Show Follow Up',
    subject: 'We missed you today — {{practice_name}}',
    body_html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px">
  <h2 style="color:#d97706">We Missed You!</h2>
  <p>Hi {{patient_first_name}},</p>
  <p>We noticed you weren't able to make it to your appointment today with <strong>{{provider_name}}</strong>.</p>
  <p>We understand things come up! Please call us at <strong>{{practice_phone}}</strong> to reschedule at a time that works better for you.</p>
  <p>Your health is important to us and we look forward to seeing you soon.</p>
  <p style="color:#6b7280;margin-top:24px">— {{practice_name}}</p>
</div>`,
    body_text: 'Hi {{patient_first_name}},\n\nWe missed you at your appointment today with {{provider_name}}.\n\nPlease call us at {{practice_phone}} to reschedule.\n\nYour health is important to us!\n\n— {{practice_name}}',
  },
];

const DEFAULT_VOICE_SCRIPTS = [
  {
    script_name: 'Appointment Reminder Call',
    script_type: 'nova_sonic',
    system_prompt: `You are a friendly, professional appointment reminder assistant for {{practice_name}}. You are calling {{patient_first_name}} {{patient_last_name}} to remind them about their upcoming appointment.

APPOINTMENT DETAILS:
- Date: {{appointment_date}}
- Time: {{appointment_time}}
- Provider: {{provider_name}}
- Department: {{department_name}}

YOUR TASK:
1. Greet the patient warmly by name.
2. Identify yourself as calling from {{practice_name}}.
3. Remind them about the upcoming appointment details.
4. Ask if they plan to keep the appointment.
5. If they need to reschedule or cancel, offer to transfer them or let them know the office number ({{practice_phone}}).
6. Thank them and wish them a good day.

STYLE: Be warm, concise, and professional. Keep the call under 60 seconds. Do not ask for sensitive medical information. If you reach voicemail, leave a brief reminder message.`,
  },
  {
    script_name: 'Cancellation Follow Up Call',
    script_type: 'nova_sonic',
    system_prompt: `You are a caring appointment follow-up assistant for {{practice_name}}. You are calling {{patient_first_name}} {{patient_last_name}} because their appointment was canceled.

CANCELED APPOINTMENT:
- Date: {{appointment_date}}
- Time: {{appointment_time}}
- Provider: {{provider_name}}

YOUR TASK:
1. Greet the patient warmly by name.
2. Identify yourself as calling from {{practice_name}}.
3. Let them know their appointment was canceled and you'd like to help them reschedule.
4. Ask if they'd like to book a new appointment.
5. If yes, offer to transfer them to scheduling or provide the office number ({{practice_phone}}).
6. Thank them for their time.

STYLE: Be empathetic and helpful. Don't pressure them. Keep it brief and professional.`,
  },
  {
    script_name: 'No-Show Follow Up Call',
    script_type: 'nova_sonic',
    system_prompt: `You are a caring appointment follow-up assistant for {{practice_name}}. You are calling {{patient_first_name}} {{patient_last_name}} because they missed their appointment.

MISSED APPOINTMENT:
- Date: {{appointment_date}}
- Time: {{appointment_time}}
- Provider: {{provider_name}}

YOUR TASK:
1. Greet the patient warmly by name.
2. Identify yourself as calling from {{practice_name}}.
3. Mention you noticed they weren't able to make it to their appointment — do NOT use the word "no-show."
4. Ask if everything is okay and if they'd like to reschedule.
5. If yes, offer to transfer them to scheduling or provide the office number ({{practice_phone}}).
6. Thank them and express that their health matters to you.

STYLE: Be warm, non-judgmental, and caring. Never guilt-trip the patient. Keep the call brief and supportive.`,
  },
  {
    script_name: 'Reschedule Confirmation Call',
    script_type: 'nova_sonic',
    system_prompt: `You are a friendly appointment assistant for {{practice_name}}. You are calling {{patient_first_name}} {{patient_last_name}} to confirm their rescheduled appointment.

NEW APPOINTMENT DETAILS:
- Date: {{appointment_date}}
- Time: {{appointment_time}}
- Provider: {{provider_name}}
- Department: {{department_name}}

YOUR TASK:
1. Greet the patient warmly by name.
2. Identify yourself as calling from {{practice_name}}.
3. Confirm the new appointment date, time, and provider.
4. Ask if this time still works for them.
5. If it doesn't work, offer to transfer to scheduling or provide the office number ({{practice_phone}}).
6. Remind them to arrive 10-15 minutes early.
7. Thank them and wish them well.

STYLE: Be cheerful, concise, and professional.`,
  },
];

// =====================================================
// Templates Tab
// =====================================================

function TemplatesTab({ practiceId }) {
  const [subTab, setSubTab] = useState('email');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({});
  const [templateSearch, setTemplateSearch] = useState('');

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getNotificationTemplates(practiceId, subTab);
      setTemplates(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [practiceId, subTab]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const [seeding, setSeeding] = useState(false);

  const clearMessages = () => { setError(null); setSuccess(null); };

  const seedDefaults = async () => {
    if (!window.confirm('This will create all default SMS templates, email templates, and voice scripts. Existing templates will not be affected. Continue?')) return;
    clearMessages();
    setSeeding(true);
    try {
      let created = 0;
      for (const tpl of DEFAULT_SMS_TEMPLATES) {
        await api.createNotificationTemplate(practiceId, { channel: 'sms', ...tpl });
        created++;
      }
      for (const tpl of DEFAULT_EMAIL_TEMPLATES) {
        await api.createNotificationTemplate(practiceId, { channel: 'email', ...tpl });
        created++;
      }
      for (const tpl of DEFAULT_VOICE_SCRIPTS) {
        await api.createNotificationTemplate(practiceId, { channel: 'voice', ...tpl });
        created++;
      }
      setSuccess(`Created ${created} default templates (${DEFAULT_SMS_TEMPLATES.length} SMS, ${DEFAULT_EMAIL_TEMPLATES.length} Email, ${DEFAULT_VOICE_SCRIPTS.length} Voice)`);
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to seed default templates');
    } finally {
      setSeeding(false);
    }
  };

  const getEmptyForm = () => {
    if (subTab === 'email') return { template_name: '', subject: '', body_html: '', body_text: '' };
    if (subTab === 'sms') return { template_name: '', message_body: '' };
    return { script_name: '', script_type: 'nova_sonic', system_prompt: '' };
  };

  const openCreate = () => {
    clearMessages();
    setEditingTemplate(null);
    setForm(getEmptyForm());
    setShowModal(true);
  };

  const openEdit = (tpl) => {
    clearMessages();
    setEditingTemplate(tpl);
    if (subTab === 'email') {
      setForm({ template_name: tpl.template_name || '', subject: tpl.subject || '', body_html: tpl.body_html || '', body_text: tpl.body_text || '' });
    } else if (subTab === 'sms') {
      setForm({ template_name: tpl.template_name || '', message_body: tpl.message_body || '' });
    } else {
      setForm({ script_name: tpl.script_name || '', script_type: tpl.script_type || 'nova_sonic', system_prompt: tpl.system_prompt || '' });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    clearMessages();
    try {
      const data = { channel: subTab, ...form };
      if (editingTemplate) {
        await api.updateNotificationTemplate(practiceId, editingTemplate.id, data);
        setSuccess('Template updated');
      } else {
        await api.createNotificationTemplate(practiceId, data);
        setSuccess('Template created');
      }
      setShowModal(false);
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to save template');
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.template_name || tpl.script_name}"?`)) return;
    clearMessages();
    try {
      await api.deleteNotificationTemplate(practiceId, tpl.id, subTab);
      setSuccess('Template deleted');
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const subTabs = [
    { key: 'email', label: 'Email' },
    { key: 'sms', label: 'SMS' },
    { key: 'voice', label: 'Voice' },
  ];

  const renderVariablesHelper = () => (
    <div style={styles.helperText}>
      Available variables: {TEMPLATE_VARIABLES.map((v, i) => (
        <code key={i} style={{ background: '#e5e7eb', padding: '1px 5px', borderRadius: 3, marginRight: 4, fontSize: 11 }}>{v}</code>
      ))}
    </div>
  );

  const renderForm = () => {
    if (subTab === 'email') {
      return (
        <>
          <div style={styles.formGroup}>
            <label style={styles.label}>Template Name</label>
            <input style={styles.input} value={form.template_name} onChange={e => setForm(p => ({ ...p, template_name: e.target.value }))} placeholder="e.g. Appointment Reminder Email" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Subject</label>
            <input style={styles.input} value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. Reminder: Your upcoming appointment" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Body (HTML)</label>
            <textarea style={{ ...styles.textarea, minHeight: 150, fontFamily: 'monospace' }} value={form.body_html} onChange={e => setForm(p => ({ ...p, body_html: e.target.value }))} placeholder="<html>...</html>" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Body (Plain Text)</label>
            <textarea style={styles.textarea} value={form.body_text} onChange={e => setForm(p => ({ ...p, body_text: e.target.value }))} placeholder="Plain text version..." />
          </div>
          {renderVariablesHelper()}
        </>
      );
    }

    if (subTab === 'sms') {
      const charCount = (form.message_body || '').length;
      return (
        <>
          <div style={styles.formGroup}>
            <label style={styles.label}>Template Name</label>
            <input style={styles.input} value={form.template_name} onChange={e => setForm(p => ({ ...p, template_name: e.target.value }))} placeholder="e.g. Appointment Reminder SMS" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Message Body</label>
            <textarea style={styles.textarea} value={form.message_body} onChange={e => setForm(p => ({ ...p, message_body: e.target.value }))} placeholder="Hi {{patient_first_name}}, ..." />
            <div style={{ ...styles.helperText, display: 'flex', justifyContent: 'space-between' }}>
              <span>{charCount} / 160 characters {charCount > 160 ? `(${Math.ceil(charCount / 153)} segments)` : ''}</span>
            </div>
          </div>
          {renderVariablesHelper()}
        </>
      );
    }

    // Voice
    return (
      <>
        <div style={styles.formGroup}>
          <label style={styles.label}>Script Name</label>
          <input style={styles.input} value={form.script_name} onChange={e => setForm(p => ({ ...p, script_name: e.target.value }))} placeholder="e.g. Appointment Reminder Voice" />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Script Type</label>
          <select style={styles.select} value={form.script_type} onChange={e => setForm(p => ({ ...p, script_type: e.target.value }))}>
            <option value="nova_sonic">Nova Sonic</option>
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>System Prompt</label>
          <textarea style={{ ...styles.textarea, minHeight: 180 }} value={form.system_prompt} onChange={e => setForm(p => ({ ...p, system_prompt: e.target.value }))} placeholder="You are a friendly medical office assistant..." />
        </div>
        {renderVariablesHelper()}
      </>
    );
  };

  const getTemplateName = (tpl) => tpl.template_name || tpl.script_name || 'Unnamed';

  const filteredTemplates = templateSearch.trim()
    ? templates.filter(t => {
        const q = templateSearch.toLowerCase();
        return getTemplateName(t).toLowerCase().includes(q) ||
          (t.subject || '').toLowerCase().includes(q) ||
          (t.message_body || '').toLowerCase().includes(q) ||
          (t.body_html || '').toLowerCase().includes(q) ||
          (t.body_text || '').toLowerCase().includes(q) ||
          (t.system_prompt || '').toLowerCase().includes(q) ||
          (t.script_type || '').toLowerCase().includes(q);
      })
    : templates;

  return (
    <div>
      <div style={styles.subTabBar}>
        {subTabs.map(st => (
          <button
            key={st.key}
            onClick={() => { setSubTab(st.key); clearMessages(); setTemplateSearch(''); }}
            style={{ ...styles.subTab, ...(subTab === st.key ? styles.subTabActive : {}) }}
          >
            {st.label}
          </button>
        ))}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h3 style={styles.sectionTitle}>
          {subTab === 'email' ? 'Email Templates' : subTab === 'sms' ? 'SMS Templates' : 'Voice Scripts'}
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
          <input
            style={{ ...styles.input, maxWidth: 240, margin: 0 }}
            placeholder="Search templates..."
            value={templateSearch}
            onChange={e => setTemplateSearch(e.target.value)}
          />
          <button style={{ ...styles.btn, ...styles.btnSuccess }} onClick={seedDefaults} disabled={seeding}>
            {seeding ? 'Seeding...' : 'Seed Defaults'}
          </button>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={openCreate}>
            + Create {subTab === 'voice' ? 'Script' : 'Template'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 20, color: '#6b7280' }}>Loading templates...</div>
      ) : filteredTemplates.length === 0 ? (
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>{templates.length === 0 ? `No ${subTab} templates found. Create your first one.` : 'No templates match your search.'}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                {subTab === 'email' && <th style={styles.th}>Subject</th>}
                {subTab === 'sms' && <th style={styles.th}>Message (preview)</th>}
                {subTab === 'voice' && <th style={styles.th}>Script Type</th>}
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((tpl, idx) => (
                <tr key={tpl.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                  <td style={styles.td}>{getTemplateName(tpl)}</td>
                  {subTab === 'email' && <td style={styles.td}>{tpl.subject || '--'}</td>}
                  {subTab === 'sms' && (
                    <td style={{ ...styles.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tpl.message_body || '--'}
                    </td>
                  )}
                  {subTab === 'voice' && <td style={styles.td}>{tpl.script_type || '--'}</td>}
                  <td style={styles.td}>
                    <div style={styles.flexRow}>
                      <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnSecondary }} onClick={() => openEdit(tpl)}>Edit</button>
                      <button style={{ ...styles.btn, ...styles.btnSmall, ...styles.btnDanger }} onClick={() => handleDelete(tpl)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.overlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {editingTemplate ? 'Edit' : 'Create'} {subTab === 'voice' ? 'Voice Script' : subTab === 'sms' ? 'SMS Template' : 'Email Template'}
            </h3>
            {renderForm()}
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.modalActions}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleSave}>
                {editingTemplate ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// Consent Tab
// =====================================================

function ConsentTab({ practiceId }) {
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const LIMIT = 50;

  const loadStats = useCallback(async () => {
    try {
      const res = await api.getConsentStats(practiceId);
      setStats(res.data || null);
    } catch (err) {
      console.error('Failed to load consent stats:', err);
    }
  }, [practiceId]);

  const loadPatients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getConsentPatients(practiceId, { search, limit: LIMIT, offset });
      setPatients(res.data?.patients || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [practiceId, search, offset]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setOffset(0);
  };

  const getConsentBadge = (status) => {
    if (status === 'OPT_IN') return { ...styles.badge, ...styles.badgeGreen };
    if (status === 'OPT_OUT') return { ...styles.badge, ...styles.badgeRed };
    return { ...styles.badge, ...styles.badgeGray };
  };

  const getConsentLabel = (status) => {
    if (status === 'OPT_IN') return 'OPT IN';
    if (status === 'OPT_OUT') return 'OPT OUT';
    return 'UNKNOWN';
  };

  const handleToggleConsent = async (patient, consentType, currentStatus) => {
    const newStatus = currentStatus === 'OPT_IN' ? 'OPT_OUT' : 'OPT_IN';
    try {
      await api.updatePatientConsent(practiceId, patient.id, consentType, { status: newStatus, source: 'MANUAL' });
      await loadPatients();
      await loadStats();
    } catch (err) {
      setError(err.message || 'Failed to update consent');
    }
  };

  const getByType = (type, status) => {
    if (!stats || !stats.byType || !stats.byType[type]) return 0;
    return stats.byType[type][status] || 0;
  };

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div>
      {error && <div style={styles.error}>{error}</div>}

      {stats && (
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{stats.totalPatients || 0}</div>
            <div style={styles.summaryLabel}>Total Patients</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, color: '#059669' }}>{getByType('NOTIFICATION_SMS', 'OPT_IN')}</div>
            <div style={styles.summaryLabel}>SMS Opted In</div>
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2 }}>{getByType('NOTIFICATION_SMS', 'OPT_OUT')} opted out</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, color: '#059669' }}>{getByType('NOTIFICATION_EMAIL', 'OPT_IN')}</div>
            <div style={styles.summaryLabel}>Email Opted In</div>
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2 }}>{getByType('NOTIFICATION_EMAIL', 'OPT_OUT')} opted out</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, color: '#059669' }}>{getByType('NOTIFICATION_VOICE', 'OPT_IN')}</div>
            <div style={styles.summaryLabel}>Voice Opted In</div>
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2 }}>{getByType('NOTIFICATION_VOICE', 'OPT_OUT')} opted out</div>
          </div>
        </div>
      )}

      <div style={styles.searchBar}>
        <input
          style={{ ...styles.input, maxWidth: 400 }}
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={handleSearch}
        />
      </div>

      {loading ? (
        <div style={{ padding: 20, color: '#6b7280' }}>Loading patients...</div>
      ) : patients.length === 0 ? (
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>No patients found.</p>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Notification SMS</th>
                  <th style={styles.th}>Notification Email</th>
                  <th style={styles.th}>Notification Voice</th>
                  <th style={styles.th}>Source</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((pt, idx) => (
                  <tr key={pt.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{pt.first_name} {pt.last_name}</td>
                    <td style={styles.td}>{pt.phone || '--'}</td>
                    <td style={styles.td}>{pt.email || '--'}</td>
                    <td style={styles.td}>
                      <span
                        style={{ ...getConsentBadge(pt.notification_sms), cursor: 'pointer' }}
                        onClick={() => handleToggleConsent(pt, 'NOTIFICATION_SMS', pt.notification_sms)}
                        title="Click to toggle"
                      >
                        {getConsentLabel(pt.notification_sms)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{ ...getConsentBadge(pt.notification_email), cursor: 'pointer' }}
                        onClick={() => handleToggleConsent(pt, 'NOTIFICATION_EMAIL', pt.notification_email)}
                        title="Click to toggle"
                      >
                        {getConsentLabel(pt.notification_email)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{ ...getConsentBadge(pt.notification_voice), cursor: 'pointer' }}
                        onClick={() => handleToggleConsent(pt, 'NOTIFICATION_VOICE', pt.notification_voice)}
                        title="Click to toggle"
                      >
                        {getConsentLabel(pt.notification_voice)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        {pt.notification_sms_source || pt.notification_email_source || pt.notification_voice_source || '--'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.pagination}>
            <button
              style={{ ...styles.btn, ...styles.btnSecondary }}
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Page {currentPage} of {totalPages || 1} ({total} total)
            </span>
            <button
              style={{ ...styles.btn, ...styles.btnSecondary }}
              disabled={offset + LIMIT >= total}
              onClick={() => setOffset(offset + LIMIT)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// =====================================================
// Activity Tab
// =====================================================

function ActivityTab({ practiceId }) {
  const { selectedPractice } = usePractice();
  // Same default as Appointments.js. A practice with no timezone set renders in
  // Eastern rather than silently in the viewer's zone.
  const practiceTimezone = selectedPractice?.timezone || 'America/New_York';
  const [evaluations, setEvaluations] = useState([]);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evalStatusFilter, setEvalStatusFilter] = useState('');
  const [notifStatusFilter, setNotifStatusFilter] = useState('');
  const [notifChannelFilter, setNotifChannelFilter] = useState('');
  const [sendNowOpen, setSendNowOpen] = useState(false);
  const [sendNowForm, setSendNowForm] = useState({
    searchQuery: '',
    appointment_id: '',
    channel: 'sms',
    template_id: '',
  });
  const [sendNowAppointments, setSendNowAppointments] = useState([]);
  const [sendNowTemplates, setSendNowTemplates] = useState([]);
  const [sendNowLoading, setSendNowLoading] = useState(false);
  const [sendNowResult, setSendNowResult] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getNotificationHistory(practiceId);
      setEvaluations(res.data?.evaluations || []);
      // getHistory returns { evaluations, scheduled } — reading
      // `scheduledNotifications` silently yielded [] and the table below has
      // therefore rendered "No scheduled notifications yet." since it was written.
      // This is the only screen in the product with a Scheduled At column, so
      // queued reminders were invisible everywhere.
      setScheduledNotifications(res.data?.scheduled || []);
    } catch (err) {
      setError(err.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const searchAppointments = async () => {
    if (!sendNowForm.searchQuery.trim()) return;
    try {
      const res = await api.searchAppointmentsForNotification(practiceId, { q: sendNowForm.searchQuery });
      setSendNowAppointments(res.data || []);
    } catch {}
  };

  const loadSendNowTemplates = async (channel) => {
    try {
      const res = await api.getNotificationTemplates(practiceId, channel);
      setSendNowTemplates(res.data || []);
    } catch {}
  };

  useEffect(() => {
    if (sendNowOpen) loadSendNowTemplates(sendNowForm.channel);
  }, [sendNowForm.channel, sendNowOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendNow = async () => {
    if (!sendNowForm.appointment_id) { setError('Select an appointment'); return; }
    setSendNowLoading(true);
    setSendNowResult(null);
    try {
      const result = await api.sendNotificationNow(practiceId, {
        appointment_id: parseInt(sendNowForm.appointment_id),
        channel: sendNowForm.channel,
        template_id: sendNowForm.template_id ? parseInt(sendNowForm.template_id) : null,
      });
      setSendNowResult({ success: true, message: `Notification ${result.data?.result?.status || 'sent'} successfully` });
      loadHistory(); // refresh activity
    } catch (err) {
      setSendNowResult({ success: false, message: err.message || 'Failed to send' });
    } finally {
      setSendNowLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      MATCHED: styles.badgeGreen,
      NO_RULE: styles.badgeGray,
      PENDING: styles.badgeAmber,
      SENT: styles.badgeGreen,
      FAILED: styles.badgeRed,
      SUPPRESSED: styles.badgeGray,
      CANCELLED: styles.badgeRed,
    };
    return { ...styles.badge, ...(map[status] || styles.badgeGray) };
  };

  // Rendered in the PRACTICE's timezone, with the zone shown. toLocaleString() uses
  // the viewer's zone and names it nowhere: a tester in IST read an 08:30 UTC row as
  // "2:00 PM" and reported the reminder as mistimed when it was correct (QA
  // 2026-10-09). Whoever is reading this table wants to know when the patient gets
  // the message, which is practice-local, not where the reader happens to be.
  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString([], {
        timeZone: practiceTimezone,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      });
    } catch {
      // An unknown IANA zone throws — fall back rather than blanking the column.
      try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
    }
  };

  if (loading) return <div style={{ padding: 20, color: '#6b7280' }}>Loading activity...</div>;

  return (
    <div>
      {error && <div style={styles.error}>{error}</div>}

      {/* Quick Send */}
      <div style={{ ...styles.card, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setSendNowOpen(!sendNowOpen)}>
          <h3 style={{ ...styles.sectionTitle, margin: 0 }}>Quick Send</h3>
          <span style={{ fontSize: 18, color: '#6b7280' }}>{sendNowOpen ? '\u25B2' : '\u25BC'}</span>
        </div>

        {sendNowOpen && (
          <div style={{ marginTop: 16 }}>
            {sendNowResult && (
              <div style={sendNowResult.success ? styles.success : styles.error}>
                {sendNowResult.message}
              </div>
            )}

            {/* Search appointments */}
            <div style={{ ...styles.formGroup }}>
              <label style={styles.label}>Search Patient/Appointment</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...styles.input, flex: 1 }}
                  value={sendNowForm.searchQuery}
                  onChange={e => setSendNowForm(p => ({ ...p, searchQuery: e.target.value }))}
                  placeholder="Type patient name..."
                  onKeyDown={e => e.key === 'Enter' && searchAppointments()} />
                <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={searchAppointments}>Search</button>
              </div>
            </div>

            {/* Appointment dropdown */}
            {sendNowAppointments.length > 0 && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Appointment</label>
                <select style={styles.select}
                  value={sendNowForm.appointment_id}
                  onChange={e => setSendNowForm(p => ({ ...p, appointment_id: e.target.value }))}>
                  <option value="">-- Select --</option>
                  {sendNowAppointments.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.patient_name} - {a.appointment_type || 'N/A'} - {(() => { const pts = String(a.appointment_date).split('T')[0].split('-').map(Number); return pts.length === 3 ? new Date(Date.UTC(pts[0], pts[1]-1, pts[2], 12)).toLocaleDateString('en-US', { timeZone: 'UTC' }) : a.appointment_date; })()} {a.provider_name ? `(${a.provider_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Channel + Template */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Channel</label>
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  {['sms', 'email', 'voice'].map(ch => (
                    <label key={ch} style={{ ...styles.checkboxRow, cursor: 'pointer' }}>
                      <input type="radio" name="sendNowChannel"
                        checked={sendNowForm.channel === ch}
                        onChange={() => setSendNowForm(p => ({ ...p, channel: ch, template_id: '' }))} />
                      {ch.toUpperCase()}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Template</label>
                <select style={styles.select}
                  value={sendNowForm.template_id}
                  onChange={e => setSendNowForm(p => ({ ...p, template_id: e.target.value }))}>
                  <option value="">Default message</option>
                  {sendNowTemplates.map(t => (
                    <option key={t.id} value={t.id}>{t.template_name || t.script_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <button style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={handleSendNow} disabled={sendNowLoading || !sendNowForm.appointment_id}>
              {sendNowLoading ? 'Sending...' : 'Send Now'}
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={styles.sectionTitle}>Recent Evaluations</h3>
        <select style={{ ...styles.select, maxWidth: 200 }} value={evalStatusFilter} onChange={e => setEvalStatusFilter(e.target.value)}>
          <option value="">All Results</option>
          <option value="MATCHED">Matched</option>
          <option value="MATCHED_NO_ACTIONS">Matched, no actions</option>
          <option value="NO_RULE">No Rule</option>
        </select>
      </div>
      {(() => {
        const filteredEvals = evalStatusFilter ? evaluations.filter(e => e.result === evalStatusFilter) : evaluations;
        return filteredEvals.length === 0 ? (
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>{evaluations.length === 0 ? 'No evaluations yet.' : 'No evaluations match filter.'}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 28 }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Patient</th>
                <th style={styles.th}>Appointment Type</th>
                <th style={styles.th}>Result</th>
                <th style={styles.th}>Rule Name</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvals.map((ev, idx) => (
                <tr key={ev.id || idx} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                  <td style={styles.td}>{formatDate(ev.created_at || ev.evaluated_at)}</td>
                  <td style={styles.td}>{ev.patient_name || `${ev.patient_first_name || ''} ${ev.patient_last_name || ''}`.trim() || '--'}</td>
                  <td style={styles.td}>{ev.appointment_type || '--'}</td>
                  <td style={styles.td}>
                    <span style={getStatusBadge(ev.result)}>
                      {ev.result || '--'}
                    </span>
                  </td>
                  <td style={styles.td}>{ev.rule_name || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={styles.sectionTitle}>Scheduled Notifications</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={{ ...styles.select, maxWidth: 160 }} value={notifStatusFilter} onChange={e => setNotifStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="SENT">Sent</option>
            <option value="FAILED">Failed</option>
            <option value="SUPPRESSED">Suppressed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <select style={{ ...styles.select, maxWidth: 140 }} value={notifChannelFilter} onChange={e => setNotifChannelFilter(e.target.value)}>
            <option value="">All Channels</option>
            <option value="SMS">SMS</option>
            <option value="EMAIL">Email</option>
            <option value="VOICE">Voice</option>
          </select>
        </div>
      </div>
      {(() => {
        // channel is stored lower-case ('sms'/'email'/'voice' from
        // _getChannelFromAction) while the options below are upper-case, so an
        // exact match emptied the table whenever a channel was picked. Compared
        // case-insensitively rather than by changing the option values, so it
        // holds whichever case the row carries. status is upper-case on both sides.
        const filteredNotifs = scheduledNotifications.filter(n =>
          (!notifStatusFilter || n.status === notifStatusFilter) &&
          (!notifChannelFilter || String(n.channel || '').toUpperCase() === notifChannelFilter)
        );
        return filteredNotifs.length === 0 ? (
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>{scheduledNotifications.length === 0 ? 'No scheduled notifications yet.' : 'No notifications match filters.'}</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Patient</th>
                <th style={styles.th}>Channel</th>
                <th style={styles.th}>Action Type</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Scheduled At</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotifs.map((notif, idx) => (
                <tr key={notif.id || idx} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                  <td style={styles.td}>{formatDate(notif.created_at)}</td>
                  <td style={styles.td}>{notif.patient_name || `${notif.patient_first_name || ''} ${notif.patient_last_name || ''}`.trim() || '--'}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...styles.badgeIndigo }}>{notif.channel || '--'}</span>
                  </td>
                  <td style={styles.td}>{formatActionType(notif.action_type)}</td>
                  <td style={styles.td}>
                    <span style={getStatusBadge(notif.status)}>
                      {notif.status || '--'}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDate(notif.scheduled_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      })()}
    </div>
  );
}

// =====================================================
// Main NotificationCenter Component
// =====================================================

function NotificationCenter({ practiceId }) {
  const [activeTab, setActiveTab] = useState('rules');

  const tabs = [
    { key: 'rules', label: 'Rules' },
    { key: 'templates', label: 'Templates' },
    { key: 'consent', label: 'Consent' },
    { key: 'activity', label: 'Activity' },
  ];

  if (!practiceId) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Notification Center</h2>
        </div>
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Please select a practice to manage notifications.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Notification Center</h2>
      </div>

      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'rules' && <RulesTab practiceId={practiceId} />}
      {activeTab === 'templates' && <TemplatesTab practiceId={practiceId} />}
      {activeTab === 'consent' && <ConsentTab practiceId={practiceId} />}
      {activeTab === 'activity' && <ActivityTab practiceId={practiceId} />}
    </div>
  );
}

const setupBannerS = {
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '16px 20px',
    marginBottom: 20,
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#dbeafe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: 15, fontWeight: 600, color: '#1e40af' },
  desc: { margin: '4px 0 0', fontSize: 13, color: '#3b82f6', lineHeight: 1.4 },
  actions: { display: 'flex', gap: 8, flexShrink: 0 },
  primaryBtn: {
    padding: '8px 18px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  dismissBtn: {
    padding: '8px 14px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
};

export default NotificationCenter;
